import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, useParams, useLocation } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import { useCategories } from "@/hooks/useCategories";
import { useLocations } from "@/hooks/useLocations";
import useAdUploads from "@/hooks/useAdUploads";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import PlanUsageBanner from "@/components/common/PlanUsageBanner";
import ImportFromLinkCard from "@/components/post-ad/ImportFromLinkCard";
import CategoryBreadcrumbChip from "@/components/common/CategoryBreadcrumbChip";
import { POST } from "@/constants/testIds";
import { buildAdPath } from "@/lib/slugs";

import CategorySection from "@/components/post-ad/sections/CategorySection";
import AiWriterSection from "@/components/post-ad/sections/AiWriterSection";
import BasicsSection from "@/components/post-ad/sections/BasicsSection";
import TimelinePriceSection from "@/components/post-ad/sections/TimelinePriceSection";
import PhotosSection from "@/components/post-ad/sections/PhotosSection";
import MediaSection from "@/components/post-ad/sections/MediaSection";
import SecondaryContactSection from "@/components/post-ad/sections/SecondaryContactSection";
import AgencySection from "@/components/post-ad/sections/AgencySection";
import LocationSection from "@/components/post-ad/sections/LocationSection";
import PrimaryContactSection from "@/components/post-ad/sections/PrimaryContactSection";
import SubmitBar from "@/components/post-ad/sections/SubmitBar";
import FormSection from "@/components/post-ad/FormSection";
import SchemaAttributesSection from "@/components/post-ad/sections/SchemaAttributesSection";
import PlateLookupCard from "@/components/post-ad/PlateLookupCard";
import PhoneVerifySheet from "@/components/post-ad/PhoneVerifySheet";
import VendorPosterSection, { FinalCheckList, PRE_SUBMIT_CHECKS } from "@/components/post-ad/VendorPosterSection";
import ReviewChecklist from "@/components/post-ad/ReviewChecklist";
import { serializeOpenHomes } from "@/components/post-ad/SchemaFieldRenderer";
import { normaliseCondition } from "@/components/post-ad/ConditionTiles";
import { gaEvent } from "@/lib/ga";

const MAX_IMAGES_FALLBACK = 10;

const EMPTY_FORM = (isEdit) => ({
  title: "",
  description: "",
  category_slug: "",
  price: "",
  other_price: "",
  // Bikes mirror-form (iter-227) fields — top-level, not attributes.
  make: "",
  model: "",
  year: null,
  condition: null,
  // In edit mode we start with null so we can detect user intent to extend expiry.
  duration_months: isEdit ? null : 3,
  region: "",
  city: "",
  area: "",
  pincode: "",
  address: "",
  lat: null,
  lng: null,
  phone: "",
  whatsapp: "",
  youtube_url: "",
  video: "",
  documents: [],
  secondary_contact: "",
  secondary_contact_phone: "",
  secondary_contact_whatsapp: "",
  secondary_contact_email: "",
  secondary_contact_photo: "",
  agency_name: "",
  agency_logo: "",
  agency_license: "",
  images: [],
  attributes: {},
  open_homes: [],
});

/**
 * Post / Edit an ad. Owns the master form state and all remote mutations;
 * every visual block is delegated to a small section component under
 * `components/post-ad/sections`.
 */
export default function PostAdPage() {
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { id: editId } = useParams();
  const isEdit = !!editId;

  const { data: categories = [] } = useCategories();
  const { data: regions = [] } = useLocations();

  const [form, setForm] = useState(() => EMPTY_FORM(isEdit));
  const [aiKeywords, setAiKeywords] = useState("");
  // Iter-253 §UX: tracks which fields were populated by the plate-lookup
  // widget so we can render a subtle green "Auto-filled" dot next to
  // each field's label. Editing a field clears its entry.
  const [prefilledKeys, setPrefilledKeys] = useState({});

  const set = (patch) => setForm((s) => ({ ...s, ...patch }));
  const clearPrefill = (keys) => {
    if (!Array.isArray(keys) || keys.length === 0) return;
    setPrefilledKeys((pk) => {
      let changed = false;
      const next = { ...pk };
      for (const k of keys) {
        if (k in next) {
          delete next[k];
          changed = true;
        }
      }
      return changed ? next : pk;
    });
  };
  const cities = useMemo(
    () => regions.find((r) => r.region === form.region)?.cities || [],
    [regions, form.region]
  );

  // ITER-233: Fetch the schema for the current category. All per-category
  // form composition (top-level fields, attribute fields, condition tiles,
  // photo cap, strict-required rules) is driven by this endpoint. Nothing
  // is hardcoded per-category anymore.
  const schemaQ = useQuery({
    queryKey: ["post-form-schema", form.category_slug],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/post-form-schema", {
        params: { slug: form.category_slug },
      });
      return data;
    },
    enabled: !!form.category_slug,
    staleTime: 5 * 60_000,
  });
  const schema = schemaQ.data || null;
  const priceSection = (schema?.core_sections || []).find((s) => s.key === "price");
  const strictRequired = schema?.strict_required || [];
  const isVehicleTopLevel = (schema?.top_level_fields || []).length > 0;

  // Events uses its dedicated form — legacy deep links land here so bounce
  // to /events/post where the founder's rich editor + repeatable session
  // cards live.
  useEffect(() => {
    const c = searchParams.get("category");
    if (c === "events" || c === "event") {
      nav("/events/post", { replace: true });
    }
  }, [searchParams, nav]);

  // Prefill category from ?category=<slug> deep-link (from /sell chooser)
  useEffect(() => {
    const c = searchParams.get("category");
    if (c && c !== "events" && c !== "event" && !form.category_slug) {
      set({ category_slug: c });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Iter-352 — "Sell it in 60 seconds": the car-valuation result deep-links
  // here with ?prefill=vehicle&plate&year&make&model&odometer_km&value_low
  // &value_high. All params are optional strings — ignore empties, never
  // block the form. The value range renders as a price HINT below the
  // price input (never the price field itself).
  useEffect(() => {
    if (isEdit || searchParams.get("prefill") !== "vehicle") return;
    const patch = { category_slug: "cars" };
    const marks = {};
    const year = searchParams.get("year") || "";
    const make = searchParams.get("make") || "";
    const model = searchParams.get("model") || "";
    if (make) {
      patch.make = make;
      marks.make = true;
    }
    if (model) {
      patch.model = model;
      marks.model = true;
    }
    if (/^\d{4}$/.test(year)) {
      patch.year = Number(year);
      marks.year = true;
    }
    const title = [year, make, model].filter(Boolean).join(" ").trim();
    if (title) {
      patch.title = title;
      marks.title = true;
    }
    const odo = Number(searchParams.get("odometer_km"));
    if (Number.isFinite(odo) && odo > 0) {
      // Cars schema attribute key for odometer is "Kilometres".
      patch.attributes = { Kilometres: odo };
      marks["attributes.Kilometres"] = true;
    }
    // Plate lands in the PlateLookupCard input (its sessionStorage seed) so
    // one tap on "Get started" pulls the full CarJam spec sheet.
    const plate = (searchParams.get("plate") || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
    if (plate) {
      try {
        sessionStorage.setItem("allsale.plateLookup.lastPlate", plate);
      } catch {
        /* sessionStorage unavailable — silent no-op */
      }
    }
    set(patch);
    if (Object.keys(marks).length) setPrefilledKeys((pk) => ({ ...pk, ...marks }));
  }, []);

  // Iter-352 — market-range hint shown under the price input.
  const vehiclePriceHint = useMemo(() => {
    if (isEdit || searchParams.get("prefill") !== "vehicle") return null;
    const fmt = (n) => `$${new Intl.NumberFormat("en-NZ").format(n)}`;
    const low = Number(searchParams.get("value_low"));
    const high = Number(searchParams.get("value_high"));
    const okLow = Number.isFinite(low) && low > 0;
    const okHigh = Number.isFinite(high) && high > 0;
    if (okLow && okHigh) return `Similar cars: ${fmt(low)}–${fmt(high)}`;
    if (okLow || okHigh) return `Similar cars: around ${fmt(okLow ? low : high)}`;
    return null;
  }, [searchParams, isEdit]);

  // Edit mode: fetch the existing listing and prefill the form.
  const editQ = useQuery({
    queryKey: ["listing-for-edit", editId],
    queryFn: async () => (await sharedApi.get(`/classifieds/listings/${editId}`)).data,
    enabled: isEdit,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!isEdit || !editQ.data) return;
    const l = editQ.data;
    const attrs = { ...(l.attributes || {}) };
    // Pull dedicated keys OUT of attrs into local values BEFORE setForm, so the
    // React 18 StrictMode double-invocation of the setter callback doesn't see
    // a mutated closure on the second run.
    const pluck = (key) => {
      const v = attrs[key];
      delete attrs[key];
      return v || "";
    };
    const address = pluck("Address") || pluck("ad_address");
    const secondary_contact = pluck("Secondary Contact");
    const secondary_contact_phone = pluck("Secondary Contact Phone");
    const secondary_contact_whatsapp = pluck("Secondary Contact WhatsApp");
    const secondary_contact_email = pluck("Secondary Contact Email");
    const secondary_contact_photo = pluck("Secondary Contact Photo");
    const agency_name = pluck("Agency Name");
    const agency_logo = pluck("Agency Logo");
    const agency_license = pluck("Agency License");
    setForm((s) => ({
      ...s,
      title: l.title || "",
      description: l.description || "",
      category_slug: l.category_slug || "",
      price: l.price != null ? String(l.price) : "",
      other_price: l.other_price != null ? String(l.other_price) : "",
      make: l.make || "",
      model: l.model || "",
      year: l.year ?? null,
      condition: l.condition || null,
      region: l.region || "",
      city: l.city || "",
      area: l.area || "",
      pincode: l.pincode || "",
      address,
      lat: l.lat ?? null,
      lng: l.lng ?? null,
      phone: l.contact?.phone || "",
      whatsapp: l.contact?.whatsapp || "",
      youtube_url: l.youtube_url || "",
      video: l.video || "",
      documents: l.documents || [],
      secondary_contact,
      secondary_contact_phone,
      secondary_contact_whatsapp,
      secondary_contact_email,
      secondary_contact_photo,
      agency_name,
      agency_logo,
      agency_license,
      images: l.images || [],
      // "Open Home Times" is left in attrs (one-way serialized); new slots
      // will overwrite on save.
      open_homes: [],
      attributes: attrs,
    }));
  }, [isEdit, editQ.data]);

  const aiMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/classifieds/ai/write-ad", {
        prompt: aiKeywords || form.title,
        category_slug: form.category_slug || undefined,
        country: "NZ",
      });
      return data;
    },
    onSuccess: (d) => {
      set({ title: d.title, description: d.description });
      toast.success("Your ad copy is ready!");
    },
    onError: (err) =>
      toast.error(err?.response?.data?.detail || "Draft failed — please try again."),
  });

  const suggestCatMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/classifieds/ai/suggest-category", {
        text: aiKeywords || form.title || form.description,
        country: "NZ",
      });
      return data;
    },
    onSuccess: (d) => {
      if (d?.category_slug) {
        set({ category_slug: d.category_slug });
        toast.success(`Picked: ${d.category_name || d.category_slug}`);
      } else {
        toast.info("Couldn't auto-detect a category.");
      }
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Suggestion failed"),
  });

  // Duplicate-check (spec §2.1): debounce on title change; soft warning
  // card — NEVER blocks posting.
  const [dupMatches, setDupMatches] = useState([]);
  useEffect(() => {
    const t = form.title.trim();
    if (t.length < 3) {
      setDupMatches([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const { data } = await sharedApi.post("/classifieds/listings/check-duplicate", {
          title: t,
          exclude_id: isEdit ? editId : undefined,
        });
        setDupMatches(Array.isArray(data?.matches) ? data.matches : []);
      } catch {
        setDupMatches([]);
      }
    }, 600);
    return () => clearTimeout(handle);
  }, [form.title, isEdit, editId]);

  const [showPhoneSheet, setShowPhoneSheet] = useState(false);

  // ── Iter-283 Vendor Poster ────────────────────────────────────────
  // Live-refresh the flag on every mount so a fresh grant/revoke from
  // admin is picked up without a re-login.
  const meQ = useQuery({
    queryKey: ["auth-me"],
    queryFn: async () => (await sharedApi.get("/auth/me")).data,
    // Iter-283 fix (iteration_110): revalidate on every PostAdPage mount
    // so a grant/revoke propagates without a re-login and without a
    // stale 30-second SPA cache carrying the old flag.
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
  });
  const canPostForVendors = Boolean(meQ.data?.can_post_for_vendors) && !isEdit;
  // Iter-283 fix (iteration_112): rehydrate vendor from history state so
  // Back after a successful poster submit shows the same pill again.
  const [posterVendor, setPosterVendor] = useState(
    () => location.state?.posterVendor || null,
  );
  const [checks, setChecks] = useState(() => PRE_SUBMIT_CHECKS.map(() => false));
  const [rightsRevoked, setRightsRevoked] = useState(false);
  const toggleCheck = (i) =>
    setChecks((prev) => prev.map((v, idx) => (idx === i ? !v : v)));
  const handleRightsRevoked = () => {
    if (rightsRevoked) return;
    setRightsRevoked(true);
    setPosterVendor(null);
    setChecks(PRE_SUBMIT_CHECKS.map(() => false));
    toast.error("Vendor-posting rights are not active on your account");
  };
  // Iter-283 fix (iteration_112): if a fresh /auth/me shows permission
  // has been REVOKED (or was never granted) while a history-restored
  // vendor is still hanging around, drop the vendor + reset the checks.
  // Prevents the checklist/mutation-endpoint from staying in vendor mode
  // after a true→false grant change.
  useEffect(() => {
    if (meQ.isLoading || meQ.isPending) return;
    if (!canPostForVendors && posterVendor) {
      setPosterVendor(null);
      setChecks(PRE_SUBMIT_CHECKS.map(() => false));
    }
  }, [canPostForVendors, meQ.isLoading, meQ.isPending, posterVendor]);
  // Only treat the current session as "in vendor mode" when both the
  // vendor AND the current permission are true. This is the single flag
  // both the checklist render and the mutation-endpoint router use.
  const inVendorMode = Boolean(posterVendor) && canPostForVendors;
  const showPosterSection = canPostForVendors && !rightsRevoked;
  const allChecksTicked = checks.every(Boolean);

  // ── Iter 708 Review step ─────────────────────────────────────────
  // Precheck response currently on screen (null = form view). Never
  // cached — every "Continue" re-runs the checks on the live draft.
  const [review, setReview] = useState(null);
  const [confirmedKeys, setConfirmedKeys] = useState([]);
  const [reviewNote, setReviewNote] = useState("");
  // Iter 709 — imported third-party photo the backend couldn't copy.
  const [photoImportError, setPhotoImportError] = useState(null);

  // Iter 708 — the exact POST /listings payload, shared by the precheck
  // call and the final submit so both always see the same draft.
  const buildListingBody = () => {
      const attributes = { ...(form.attributes || {}) };
      // Iter-232 §6 canonical: street address writes to `attributes.ad_address`.
      if (form.address) attributes.ad_address = form.address;
      const openHomeStr = serializeOpenHomes(form.open_homes);
      if (openHomeStr) attributes["Open Home Times"] = openHomeStr;
      if (form.secondary_contact) attributes["Secondary Contact"] = form.secondary_contact;
      if (form.secondary_contact_phone) attributes["Secondary Contact Phone"] = form.secondary_contact_phone;
      if (form.secondary_contact_whatsapp) attributes["Secondary Contact WhatsApp"] = form.secondary_contact_whatsapp;
      if (form.secondary_contact_email) attributes["Secondary Contact Email"] = form.secondary_contact_email;
      if (form.secondary_contact_photo) attributes["Secondary Contact Photo"] = form.secondary_contact_photo;
      if (form.agency_name) attributes["Agency Name"] = form.agency_name;
      if (form.agency_logo) attributes["Agency Logo"] = form.agency_logo;
      if (form.agency_license) attributes["Agency License"] = form.agency_license;

      // Iter-232 §1: canonical `contact.secondary` block mirrors what the
      // form already collects. Kept in addition to the flattened `attributes.*`
      // keys so both old and new backend contracts are satisfied.
      const hasSecondary = !!(
        form.secondary_contact ||
        form.secondary_contact_phone ||
        form.secondary_contact_whatsapp ||
        form.secondary_contact_email ||
        form.secondary_contact_photo
      );
      const secondaryObj = hasSecondary
        ? {
            name: form.secondary_contact || undefined,
            phone: form.secondary_contact_phone || undefined,
            whatsapp: form.secondary_contact_whatsapp || undefined,
            email: form.secondary_contact_email || undefined,
            photo: form.secondary_contact_photo || undefined,
          }
        : undefined;

      const body = {
        title: form.title.trim(),
        description: form.description.trim(),
        category_slug: form.category_slug,
        price: form.price ? Number(form.price) : undefined,
        currency: "NZD",
        country: "NZ",
        region: form.region || undefined,
        city: form.city || undefined,
        area: form.area || undefined,
        pincode: form.pincode || undefined,
        lat: form.lat ?? undefined,
        lng: form.lng ?? undefined,
        duration_months: form.duration_months || undefined,
        youtube_url: form.youtube_url || undefined,
        video: form.video || undefined,
        documents: form.documents.length ? form.documents : undefined,
        images: form.images,
        contact: {
          phone: form.phone || undefined,
          whatsapp: form.whatsapp || undefined,
          secondary: secondaryObj,
          allow_chat: true,
        },
        attributes: Object.keys(attributes).length ? attributes : undefined,
      };
      // Iter-233 schema-driven top-level fields (vehicles: make/model/year/
      // shipping_options). Any category whose schema declares
      // `top_level_fields` gets those keys packed onto the request body root.
      const topLevel = schema?.top_level_fields || [];
      topLevel.forEach((f) => {
        const v = form[f.key];
        if (v === "" || v === null || v === undefined) return;
        if (f.input === "number" && v !== "") body[f.key] = Number(v);
        else body[f.key] = v;
      });
      // Site-wide top-level condition + other_price when the schema exposes
      // them in the price section (Iter-232/233 — all categories).
      if (form.condition) body.condition = normaliseCondition(form.condition) || form.condition;
      if (form.other_price !== "" && form.other_price != null && !Number.isNaN(Number(form.other_price))) {
        body.other_price = Number(form.other_price);
      }
      // Iter-216: chooser forwards category/subcategory ids as query params.
      // Prefer them over the bare slug so the backend attaches to the exact
      // node the user picked. `category_slug` still works as a legacy
      // fallback.
      const catId = searchParams.get("category_id");
      const subId = searchParams.get("subcategory_id");
      if (catId) body.category_id = catId;
      if (subId) body.subcategory_id = subId;
      return body;
  };

  const submitMut = useMutation({
    mutationFn: async (vars = {}) => {
      const body = buildListingBody();
      // Iter 708 — staff checklist publish: the confirmed keys + note ride
      // on the create payload; backend auto-approves (status: active).
      if (vars.staffReview) body.staff_review = vars.staffReview;
      const { data } = isEdit
        ? await sharedApi.put(`/classifieds/listings/${editId}`, body)
        : inVendorMode
        ? await sharedApi.post("/classifieds/poster/listings", {
            user_email: posterVendor.email,
            listing: body,
          })
        : await sharedApi.post("/classifieds/listings", body);
      return data;
    },
    onSuccess: (d, vars) => {
      // Iter 741 — GA4 recommended event on successful ad creation.
      if (!isEdit) gaEvent("post_ad", { category_slug: form.category_slug });
      // Iter 708 — staff checklist publish: dedicated success copy; the ad
      // is live immediately (no Pending-review queue).
      if (vars?.staffReview) {
        toast.success("Published — approved by you (staff)");
        if (d?.staff_review?.safety_override) {
          toast.warning("Safety hold overridden by your staff review.", { duration: 7000 });
        }
        nav(buildAdPath(d));
        return;
      }
      // Safety-hold (iter-224 spec): if the backend flagged the listing for
      // manual review, show the review copy and route the seller to My Ads
      // instead of the (still-private) detail page.
      if (d?.held_for_review) {
        const msg =
          d.review_message ||
          "Your listing is under review before it goes live. This usually takes a few hours. We'll notify you once it's approved.";
        toast.info(msg, { duration: 8000 });
        nav("/my-ads");
        return;
      }
      // Handle inline duplicate_warning on create response (spec §2.1)
      const w = d?.duplicate_warning;
      if (w && Array.isArray(w.matches) && w.matches.length > 0) {
        toast.warning(
          `Heads-up: you have ${w.matches.length} similar ad${w.matches.length === 1 ? "" : "s"} live.`,
          { duration: 6000 }
        );
      }
      if (inVendorMode && !isEdit) {
        // Iter-283 §c — vendor path. Persist the vendor onto the CURRENT
        // /post-ad history entry (via replaceState) BEFORE pushing the
        // detail route, so browser Back restores /post-ad with the pill
        // already selected. nav(…, {state}) alone attaches state to the
        // destination entry, which Back cannot read (iteration_111 RCA).
        try {
          window.history.replaceState(
            { ...(window.history.state || {}), usr: { posterVendor } },
            "",
          );
        } catch { /* history API disabled — non-fatal */ }
        toast.success(`Ad posted for ${posterVendor.email} ✅`);
        setChecks(PRE_SUBMIT_CHECKS.map(() => false));
        nav(buildAdPath(d));
        return;
      }
      toast.success(isEdit ? "Ad updated." : "Your ad is live!");
      nav(buildAdPath(d));
    },
    onError: (err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Iter-283 §4 — 403 from the poster endpoint = rights were revoked
      // mid-session. Hide the section, drop the vendor, and toast once.
      if (status === 403 && inVendorMode) {
        handleRightsRevoked();
        return;
      }
      // Iter-243 first-listing phone gate. Backend returns 403 with a
      // structured `detail: {code, message}` when the user has no
      // verified phone. Show the verify sheet inline and preserve the
      // draft — auto-retry submit on success.
      if (
        status === 403 &&
        detail &&
        typeof detail === "object" &&
        detail.code === "phone_verification_required"
      ) {
        toast.info(detail.message || "Verify your mobile number to post your first listing.");
        setShowPhoneSheet(true);
        return;
      }
      const label =
        typeof detail === "string"
          ? detail
          : detail?.message || (isEdit ? "Could not save changes" : "Could not post ad");
      // Iter 709 — backend couldn't copy a third-party photo URL: surface
      // the message next to the Photos section and offer the file upload.
      if (/couldn.t copy the photo/i.test(String(label))) {
        setReview(null);
        setPhotoImportError(String(label));
        toast.error(String(label), { duration: 9000 });
        setTimeout(() => {
          document
            .querySelector("[data-testid='post-ad-photo-import-error']")
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 150);
        return;
      }
      if (status === 403 && /limit/i.test(String(label))) {
        toast.error(label, {
          action: { label: "Upgrade", onClick: () => nav("/plan-and-pricing") },
          duration: 8000,
        });
      } else {
        toast.error(String(label));
      }
    },
  });

  // Iter 708 — pre-publish checks. Runs on "Continue" with the exact
  // draft payload; the Review modal renders the response. A 404 (backend
  // not yet published on this host) falls back to the direct submit so
  // posting is never blocked by the new step.
  const precheckMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post(
        "/classifieds/listings/precheck",
        buildListingBody(),
        { timeout: 25000 },
      );
      return data;
    },
    onSuccess: (d) => {
      setConfirmedKeys([]);
      setReviewNote("");
      setReview(d);
    },
    onError: (err) => {
      if (err?.response?.status === 404) {
        submitMut.mutate({});
        return;
      }
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Couldn't run the pre-publish checks — please try again.",
      );
    },
  });

  const toggleConfirmKey = (k) =>
    setConfirmedKeys((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k],
    );

  const publishFromReview = () => {
    setPhotoImportError(null);
    if (review?.role === "staff") {
      const note = reviewNote.trim();
      submitMut.mutate({
        staffReview: { confirmed: confirmedKeys, ...(note ? { note } : {}) },
      });
    } else {
      submitMut.mutate({});
    }
  };

  const switchCategoryFromReview = (slug, name) => {
    set({ category_slug: slug });
    setReview(null);
    toast.info(`Category switched to ${name || slug} — tap Continue to re-run the checks.`);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.category_slug) {
      toast.error("Title, description and category are required.");
      return;
    }
    // Iter-283 §5 — when vendor mode is active, every checklist item must
    // be ticked before the request is fired.
    if (inVendorMode && !allChecksTicked) {
      toast.error("Please tick every item in the Final check before posting.");
      return;
    }
    // ITER-233: enforce schema-declared strict_required (e.g. bikes: region,
    // area, images[0]).
    const missingStrict = strictRequired.find((rule) => {
      if (rule === "images[0]") return !form.images.length;
      if (rule === "region") return !form.region;
      if (rule === "area") return !form.area;
      if (rule === "city") return !form.city;
      return false;
    });
    if (missingStrict) {
      const label =
        missingStrict === "images[0]"
          ? "at least one photo"
          : missingStrict;
      toast.error(`Please add ${label} for this category.`);
      return;
    }
    // Enforce schema-declared required condition (Iter-232/233).
    const conditionField = (priceSection?.fields || []).find((f) => f.key === "condition");
    if (conditionField?.required && !normaliseCondition(form.condition)) {
      toast.error("Please pick a condition for your listing.");
      const el = document.querySelector("[data-testid='post-ad-condition-group']");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const firstTile = el.querySelector("button[role='radio']");
        if (firstTile) firstTile.focus();
      }
      return;
    }
    // Iter 708 — the Review step sits between the form and Publish for
    // the standard create flow. Edits and the vendor-poster flow (which
    // has its own Final check) submit directly.
    if (isEdit || inVendorMode) {
      submitMut.mutate({});
      return;
    }
    setPhotoImportError(null);
    precheckMut.mutate();
  };

  // Public per-category photo-limit endpoint (Iter 756: cars AND property 15 / other 10)
  const photoLimitQ = useQuery({
    queryKey: ["photo-limit", form.category_slug],
    queryFn: async () =>
      (await sharedApi.get("/classifieds/photo-limit", { params: { category: form.category_slug } }))
        .data,
    enabled: !!form.category_slug,
    staleTime: 5 * 60_000,
  });
  const MAX_IMAGES = schema?.photo_limit ?? photoLimitQ.data?.limit ?? MAX_IMAGES_FALLBACK;

  // If the user picks a lower-limit category after already uploading photos,
  // silently drop the tail (mirrors the server's silent-truncation behavior).
  useEffect(() => {
    if (form.images.length > MAX_IMAGES) {
      set({ images: form.images.slice(0, MAX_IMAGES) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [MAX_IMAGES]);

  const uploads = useAdUploads({ form, set, maxImages: MAX_IMAGES });

  // Iter-624 — photos added by the LAST link-import. A re-import must not
  // keep the previous import's photos in front (that's how three different
  // cars ended up sharing one wrong images[0]); the user's own uploads stay.
  const importedImagesRef = useRef([]);

  const handleImportedExtraction = ({ listing: L, images: imgs }) => {
    const merged = { ...form };
    if (L.title) merged.title = L.title;
    if (L.description) merged.description = L.description;
    if (L.category_slug) merged.category_slug = L.category_slug;
    if (L.price != null) merged.price = String(L.price);
    if (L.region) merged.region = L.region;
    if (L.city) merged.city = L.city;
    if (L.attributes && typeof L.attributes === "object") {
      merged.attributes = { ...(merged.attributes || {}), ...L.attributes };
    }
    if (Array.isArray(imgs) && imgs.length) {
      const cap = photoLimitQ.data?.limit ?? MAX_IMAGES_FALLBACK;
      const own = (merged.images || []).filter((u) => !importedImagesRef.current.includes(u));
      const fresh = imgs.filter((u) => !own.includes(u));
      importedImagesRef.current = fresh;
      merged.images = [...own, ...fresh].slice(0, cap);
    }
    setForm(merged);
    document
      .querySelector(`[data-testid="${POST.form}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo
        title={isEdit ? "Edit your ad" : "Post your ad"}
        description="List anything on Allsale Classified — free, easy, and takes under a minute."
      />
      <Breadcrumbs items={[{ label: isEdit ? "Edit ad" : "Post your ad" }]} />

      <div className="mt-6 mb-8">
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 uppercase tracking-wide">
          {isEdit ? "Edit your ad" : "Post your ad"}
        </h1>
      </div>

      {!isEdit && <PlanUsageBanner className="mb-6" />}

      {showPosterSection && (
        <VendorPosterSection
          vendor={posterVendor}
          onChange={setPosterVendor}
          onRightsRevoked={handleRightsRevoked}
        />
      )}

      {!isEdit && (
        <ImportFromLinkCard
          disabled={submitMut.isPending}
          onExtracted={handleImportedExtraction}
        />
      )}

      <form
        data-testid={POST.form}
        onSubmit={submit}
        className="rounded-2xl bg-white border border-soft p-6 md:p-10 space-y-6 mt-6"
      >
        {/* SELECTED CATEGORY bar — mirrors the reference layout with a
            change-link so sellers can bounce back to the chooser. No
            bottom border here — the first FormSection's own top-border
            provides the divider (avoids a visual double line). */}
        <div data-testid="post-ad-selected-category">
          <div className="text-xs font-black uppercase tracking-[0.14em] text-slate-500 mb-1">
            Selected category
          </div>
          <CategoryBreadcrumbChip
            categoryId={searchParams.get("category_id")}
            subcategoryId={searchParams.get("subcategory_id")}
            fallbackLabel={form.category_slug || "Category"}
            testId="post-ad-change-category"
          />
          <p className="text-sm text-slate-500 mt-3">Include some details</p>
        </div>

        {/* Iter-252 §placement: PlateLookupCard renders at the TOP of the
            form, above the Basic Details FormSection, whenever the schema
            declares plate_lookup (backend already scopes this to Cars
            and its 7 subcategories). Keeps the "have the plate? one-tap
            prefill" affordance visible before any other input. */}
        {schema?.plate_lookup && (
          <PlateLookupCard
            plateLookupConfig={schema.plate_lookup}
            onPrefill={(prefill) => {
              const patch = {};
              const newPrefilled = {};
              // Iter-253: CarJam now returns a formatted ad-title
              // suggestion (e.g. "2017 Ford Focus SPORT 1.5P/6AT").
              // Prefill it — the seller can edit before submitting.
              if (prefill.suggested_title) {
                patch.title = prefill.suggested_title;
                newPrefilled.title = true;
              }
              if (prefill.make) {
                patch.make = prefill.make;
                newPrefilled.make = true;
              }
              if (prefill.model) {
                patch.model = prefill.model;
                newPrefilled.model = true;
              }
              if (prefill.year) {
                patch.year = prefill.year;
                newPrefilled.year = true;
              }
              if (prefill.attributes && typeof prefill.attributes === "object") {
                // ITER-246/252: CarJam returns fully-qualified strings like
                // "5-gear automatic" or "5 Door Hatch" that don't exactly
                // match schema chip options (e.g. "Automatic", "Hatchback").
                // Normalise chip-typed attributes to the closest schema
                // option so the chip renders as selected. Non-chip fields
                // (text/number/date) pass through unchanged.
                const chipFields = (schema?.attribute_fields || []).filter(
                  (f) =>
                    (f.input === "chips" || f.input === "multi") &&
                    Array.isArray(f.options)
                );
                const optionMap = new Map(chipFields.map((f) => [f.key, f.options]));
                const tok = (s) =>
                  String(s || "")
                    .toLowerCase()
                    .split(/[^a-z0-9]+/)
                    .filter(Boolean);
                const commonPrefix = (a, b) => {
                  let i = 0;
                  while (i < a.length && i < b.length && a[i] === b[i]) i++;
                  return i;
                };
                // Stem-token match: two words are "the same idea" when
                // their shared prefix is ≥ min(4, tokenLen). Catches
                // "hatch" ↔ "hatchback", "ute" ↔ "utility" without false-
                // positives across unrelated words ("wagon" ↔ "waggle" =
                // prefix 3, rejected).
                const wordStemMatch = (rawStr, optStr) => {
                  const rt = tok(rawStr);
                  const ot = tok(optStr);
                  return ot.some((o) =>
                    rt.some(
                      (r) =>
                        commonPrefix(o, r) >=
                        Math.min(4, o.length, r.length)
                    )
                  );
                };
                const normaliseChip = (raw, options) => {
                  if (raw == null || raw === "") return raw;
                  const values = Array.isArray(raw) ? raw : [raw];
                  const mapped = values.map((v) => {
                    const s = String(v).trim();
                    const exact = options.find((o) => String(o) === s);
                    if (exact !== undefined) return exact;
                    const ci = options.find(
                      (o) => String(o).toLowerCase() === s.toLowerCase()
                    );
                    if (ci !== undefined) return ci;
                    const sub = options.find((o) =>
                      s.toLowerCase().includes(String(o).toLowerCase())
                    );
                    if (sub !== undefined) return sub;
                    const stem = options.find((o) => wordStemMatch(s, o));
                    if (stem !== undefined) return stem;
                    return v;
                  });
                  return Array.isArray(raw) ? mapped : mapped[0];
                };
                const normalisedAttrs = {};
                for (const [k, v] of Object.entries(prefill.attributes)) {
                  if (optionMap.has(k)) {
                    normalisedAttrs[k] = normaliseChip(v, optionMap.get(k));
                  } else {
                    normalisedAttrs[k] = v;
                  }
                  newPrefilled[`attributes.${k}`] = true;
                }
                patch.attributes = {
                  ...(form.attributes || {}),
                  ...normalisedAttrs,
                };
              }
              set(patch);
              if (Object.keys(newPrefilled).length) {
                setPrefilledKeys((pk) => ({ ...pk, ...newPrefilled }));
              }
            }}
          />
        )}

        <FormSection title="Basic Details">
          <CategorySection
            categorySlug={form.category_slug}
            categories={categories}
            onChange={(v) => set({ category_slug: v })}
            suggestCatMut={suggestCatMut}
            canSuggest={!!(aiKeywords || form.title || form.description)}
          />

          {schema && (
            <SchemaAttributesSection
              schema={schema}
              form={form}
              set={set}
              prefilledKeys={prefilledKeys}
              clearPrefill={clearPrefill}
            />
          )}

          <AiWriterSection
            aiKeywords={aiKeywords}
            setAiKeywords={setAiKeywords}
            aiMut={aiMut}
            disabled={!aiKeywords && !form.title}
          />

          <BasicsSection
            form={form}
            set={set}
            dupMatches={dupMatches}
            prefilledKeys={prefilledKeys}
            clearPrefill={clearPrefill}
          />
        </FormSection>

        <FormSection title={priceSection?.title || "Set A Price"}>
          <TimelinePriceSection
            form={form}
            set={set}
            isEdit={isEdit}
            priceSection={priceSection}
            priceHint={vehiclePriceHint}
          />
        </FormSection>

        <FormSection title="Upload Photos">
          {photoImportError && (
            <p
              data-testid="post-ad-photo-import-error"
              className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3"
            >
              {photoImportError} You can save the photo to your device and
              upload the file below instead.
            </p>
          )}
          <PhotosSection
            form={form}
            set={set}
            maxImages={MAX_IMAGES}
            uploadImage={uploads.uploadImage}
            addImageUrl={uploads.addImageUrl}
          />

          <MediaSection
            form={form}
            set={set}
            uploadVideo={uploads.uploadVideo}
            uploadDoc={uploads.uploadDoc}
            maxDocs={uploads.MAX_DOCS}
          />
        </FormSection>

        <FormSection title="Confirm Your Location">
          <LocationSection
            form={form}
            set={set}
            regions={regions}
            cities={cities}
            isProperty={/propert|real-estate|^rent$|flatmate/.test(schema?.root?.slug || "")}
          />
        </FormSection>

        <FormSection title="Contact">
          <PrimaryContactSection form={form} set={set} />
          <SecondaryContactSection
            form={form}
            set={set}
            uploadSecondaryContactPhoto={uploads.uploadSecondaryContactPhoto}
          />
          <AgencySection form={form} set={set} uploadAgencyLogo={uploads.uploadAgencyLogo} />
        </FormSection>

        {inVendorMode && (
          <FinalCheckList checks={checks} onToggle={toggleCheck} />
        )}

        <SubmitBar
          isEdit={isEdit}
          submitting={submitMut.isPending || precheckMut.isPending}
          disabled={(isEdit && editQ.isLoading) || (inVendorMode && !allChecksTicked)}
          label={!isEdit && !inVendorMode ? "Continue" : undefined}
        />
      </form>

      {review && (
        <ReviewChecklist
          data={review}
          confirmed={confirmedKeys}
          onToggleConfirm={toggleConfirmKey}
          note={reviewNote}
          onNote={setReviewNote}
          onPublish={publishFromReview}
          publishing={submitMut.isPending}
          onBack={() => setReview(null)}
          onSwitchCategory={switchCategoryFromReview}
        />
      )}

      <PhoneVerifySheet
        open={showPhoneSheet}
        onClose={() => setShowPhoneSheet(false)}
        onVerified={() => submitMut.mutate({})}
      />

      <style>{`.input{width:100%;background:#fff;border:1px solid var(--border-soft);border-radius:14px;padding:.7rem 1rem;font-size:.95rem;color:var(--text-primary);}
      .input:focus{outline:none;border-color:var(--brand-blue);box-shadow:0 0 0 3px rgba(28,169,224,.12);}
      textarea.input{padding-top:.75rem;padding-bottom:.75rem;line-height:1.5;}`}</style>
    </div>
  );
}
